'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import PrimaryButton from '@/components/ui/PrimaryButton/PrimaryButton';
// import DevicePartnerBadge from '../DevicePartnerBadge/DevicePartnerBadge';
import CompactButton from '@/components/ui/CompactButton/CompactButton';
import { LanguageType, LanguageDirection, Partners } from '@/lib/types';
import { COLOR_ALPHA_NEUTRAL_900_ALPHA_12 } from '@/lib/variables';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import Image from 'next/image';

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(ScrollTrigger);

interface ServiceBenefitCardProps {
	title: string;
	description: string;
	imageSrc: string;
	imageAlt: string;
	poweredBy?: Partners;
	buttonText: string;
	language?: LanguageType;
	onButtonClick: () => void;
	normalWidth: string;
	expandedWidth: string;
	height: string;
}


const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
}

const ServiceBenefitCard: React.FC<ServiceBenefitCardProps> = ({
	title,
	description,
	imageSrc,
	imageAlt,
	// poweredBy,
	buttonText,
	language = 'english',
	onButtonClick,
	normalWidth = '351px',
	expandedWidth = '351px',
	height = '40.875rem',
}) => {
	const navigate = useAnimatedNavigate();
	const [isExpanded, setIsExpanded] = useState(false);
	const [showContent, setShowContent] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);
	// debug.log("poweredBy: ", poweredBy);


	const isLg = useIsLg();
	const isTb = useIsTb();

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (isExpanded) {
			timeout = setTimeout(() => {
				setShowContent(true);
			}, 250);
		} else {
			timeout = setTimeout(() => {
				setShowContent(false);
			}, 350);
		}
		let refreshTimeout = null;
		if (!isTb) {
			refreshTimeout = setTimeout(() => {
				ScrollTrigger.refresh();
			}, 1000);
		}

		return () => {
			clearTimeout(timeout);
			if (refreshTimeout) {
				clearTimeout(refreshTimeout);
			}
		};
	}, [isExpanded, isTb]);

	return (
		<div
			ref={cardRef}
			className={`
				relative
				overflow-hidden transition-all duration-500 ease-in-out
				origin-left
				border-radius-main
				outline-secondary
				outline-stroke-soft-200
				lg:origin-left
			`}
			style={{
				transformOrigin: '0% 50%',
				width: isExpanded ? expandedWidth : normalWidth,
				height: isTb ? height : (isExpanded ? '98svh' : height),
				aspectRatio: isLg
					? (isExpanded ? 'auto' : '3/4')
					: (isExpanded ? 'auto' : '0.6')
			}}
		>
			{/* Image Container */}
			<div className="relative w-full h-full overflow-hidden" style={{ backgroundColor: COLOR_ALPHA_NEUTRAL_900_ALPHA_12 }}>
				<Image
					src={imageSrc}
					alt={imageAlt}
					fill
					sizes="(min-width: 1440px) 28vw, (min-width: 992px) 40vw, (min-width: 768px) 57vw, 83vw"
					loading="eager"
					className="service-card-image"
					style={{
						scale: isExpanded ? 1.1 : 1.1,
						filter: isExpanded && isLg ? 'blur(7px)' : 'none',
						transformOrigin: '50% 50%',
						objectFit: 'cover',
					}}
				/>

				{/* Top Bar */}
				<div
					style={{
						direction: LANGUAGE_TO_DIRECTION[language],
						transform: 'translateZ(0)',
						backfaceVisibility: 'hidden',
						WebkitBackfaceVisibility: 'hidden',
					}}
					className={`
					absolute
					${isExpanded ?
							`
							top-[2rem] right-[1.5rem] left-[1.5rem]
							lg:top-[3.5rem] lg:right-[3.5rem] lg:left-[3.5rem]
							`
							:
							`
							top-[1.5rem] right-[1.25rem] left-[1.25rem]
							lg:top-[2rem] lg:right-[2rem] lg:left-[2rem]
							`
						}
					`}>
					<div className="flex items-center justify-between gap-[1rem]">
						<div className={`
							flex flex-col lg:flex-row gap-[0.5rem] flex-wrap items-start lg:items-center justify-start
							text-18-to-14--alexandria
							text-text-reversed

								${showContent || !isExpanded
								? 'opacity-100'
								: 'opacity-0'
							}
							`}>
							{/* {poweredBy && (
								<>
									<p className='whitespace-nowrap'>Powered by</p>
									<DevicePartnerBadge partner={poweredBy} />
								</>
							)} */}
						</div>
						<CompactButton size={isLg ? (isExpanded ? 64 : 56) : 44} radius={9999}
							className='relative'
							style={{
								transform: 'translateZ(0)',
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
							}}
							onClick={() => {
								setIsExpanded(!isExpanded);
								if (!isTb) {
									setTimeout(() => {
										if (!isExpanded) {
											onButtonClick();
										}
										cardRef.current?.scrollIntoView({
											behavior: 'smooth',
											block: 'center'
										});
									}, 600);
								} else {
									setTimeout(() => {
										if (!isExpanded) {
											onButtonClick();
										}
									}, 600);
								}
							}}
						>
							<div
								className="absolute inset-0 flex items-center justify-center"
								style={{
									backfaceVisibility: 'hidden',
									WebkitBackfaceVisibility: 'hidden',
								}}
							>
								<Minus
									size={isLg ? 24 : 16}
									style={{
										opacity: isExpanded ? 1 : 0,
										transform: isExpanded ? 'rotate(0deg)' : 'rotate(90deg)',
										transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out',
										backfaceVisibility: 'hidden',
										WebkitBackfaceVisibility: 'hidden',
									}}
									className="absolute text-graphics-secondary"
								/>
								<Plus
									size={isLg ? 24 : 16}
									style={{
										opacity: isExpanded ? 0 : 1,
										transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
										transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out',
										backfaceVisibility: 'hidden',
										WebkitBackfaceVisibility: 'hidden',
									}}
									className="absolute text-graphics-secondary"
								/>
							</div>
						</CompactButton>
					</div>
				</div>
			</div>

			{/* Content */}
			<div
				style={{
					direction: LANGUAGE_TO_DIRECTION[language],
					transform: 'translateZ(0)',
					backfaceVisibility: 'hidden',
					WebkitBackfaceVisibility: 'hidden',
				}}
				className={`
				absolute
					${isExpanded ?
						`
							bottom-[2rem] right-[1.5rem] left-[1.5rem]
							lg:bottom-[3.5rem] lg:right-[3.5rem] lg:left-[3.5rem]
							`
						:
						`
							bottom-[1.5rem] right-[1.25rem] left-[1.25rem]
							lg:bottom-[2rem] lg:right-[2rem] lg:left-[2rem]
							`
					}
				`}>
				{isExpanded ? (
					<div
						style={{
							paddingInlineEnd: isLg ? '3.5rem' : '0rem',
						}}
						className="flex flex-col gap-[1.5rem] max-w-[49.6875rem]">
						<h4
							className={`
							text-36-to-24--neue-june
								}
								text-text-reversed
								transition-[opacity,transform] duration-300
								${showContent
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-4'
								}`}
						>
							{title}
						</h4>
						<p className={`
								transition-[opacity,transform] duration-300
								${showContent
								? 'opacity-100 translate-y-0'
								: 'opacity-0 translate-y-4'
							}
							text-18--alexandria
							text-text-reversed
								`}>
							{description}
						</p>

						{/* reset the direction so i can pass the language explicitly */}
						<div className='w-fit' style={{ direction: "ltr" }}>
							<PrimaryButton text={buttonText} type='accent' size={isLg ? 56 : 52} language={language} onClick={() => navigate('/signup')} />
						</div>
					</div>
				) : (
					<h4
						style={{
							opacity: !showContent ? 1 : 0,
							transform: !showContent ? 'translateY(0)' : 'translateY(1rem)',
							transition: 'opacity 300ms, transform 300ms',
							backfaceVisibility: 'hidden',
							WebkitBackfaceVisibility: 'hidden',
						}}
						className="text-36-to-24--neue-june text-text-reversed"
					>
						{title}
					</h4>
				)}
			</div>
		</div >
	);
}

export default ServiceBenefitCard;
